import { GoCheck, GoX } from "react-icons/go";
import { 
  BarChart3, 
  Search, 
  GraduationCap,
  Users,
  Bot,
  Briefcase,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import { useState } from "react";

const SubscriptionCard = ({
  type,
  title,
  subtitle,
  description,
  price,
  period,
  features,
  isPopular,
  onClick,
  isSelected,
  currentPlan,
  onBillingToggle,    
  selectedBilling,
  tag,
  showMoreFeatures,
  buttonText,
  hideButtons = false
}) => {
  const [showAllFeatures, setShowAllFeatures] = useState(false);
  const showBillingToggle = type.includes("pro") || type.includes("premium");

  const getButtonText = () => type === currentPlan ? "Current Plan" : buttonText || "Get Started";

  const getCategoryIcon = (categoryName) => {
    const icons = {
      "Career Assessments": <BarChart3 size={16} className="text-purple-600" />,
      "Career Discovery": <Search size={16} className="text-purple-600" />,
      "Skills & Learning": <GraduationCap size={16} className="text-purple-600" />,
      "Mentors and Community": <Users size={16} className="text-purple-600" />,
      "AI ChatBot Support": <Bot size={16} className="text-purple-600" />,
      "Job Search Support": <Briefcase size={16} className="text-purple-600" />
    };
    return icons[categoryName] || (
      <div className="w-4 h-4 bg-purple-200 rounded flex items-center justify-center">
        <div className="w-2 h-2 bg-purple-600 rounded"></div>
      </div>
    );
  };

  const renderFeature = (feature, index) => {
    const value = feature.value;
    const normalized = value?.toString().toLowerCase();
    const isYes = normalized === "yes" || normalized === "true";
    const isNo = normalized === "no" || normalized === "false";

    return (
      <li key={index} className="flex items-center justify-between">
        <span className={`text-sm ${isNo ? "text-gray-500" : "text-gray-700"}`}>
          {feature.text}
          {feature.note && <span className="ml-1 text-xs text-gray-500">({feature.note})</span>}
        </span>
        <div className="flex-shrink-0">
          {isYes ? <GoCheck className="text-green-500" size={16} /> : isNo ? <GoX className="text-red-500" size={16} /> : null}
        </div>
      </li>
    );
  };

  const renderCategory = (category, categoryIndex) => (
    <div key={categoryIndex}>
      <h4 className="text-sm font-semibold text-purple-800 mb-3 flex items-center">
        {getCategoryIcon(category.category)}
        <span className="ml-2">{category.category}</span>
      </h4>
      <ul className="space-y-2">
        {category.items.map(renderFeature)}
      </ul>
    </div>
  );

  const initialFeatures = features.slice(0, 3);
  const additionalFeatures = features.slice(3);

  return (
    <div
      className={`bg-white rounded-xl shadow-lg overflow-hidden border-2 ${
        isSelected ? "border-purple-500" : isPopular ? "border-purple-500" : "border-gray-200"
      } flex flex-col h-full transition-all duration-300 hover:scale-105 relative`}
    >
      <div className="p-6 border-b border-gray-100 relative">
        {isPopular && (
          <div className="absolute top-0 left-0 bg-red-500 text-white text-xs px-2 py-1 rounded-bl-lg font-medium uppercase tracking-wide">
            Most Popular
          </div>
        )}

        {tag && (
          <div className="inline-block bg-red-500 text-white text-xs px-2 py-1 rounded mb-3">
            {tag}
          </div>
        )}

        <h1 className="text-center text-2xl font-bold text-black mb-1">{title}</h1>
        <h3 className="text-center text-sm text-purple-600 mb-2">{subtitle}</h3>
        
        <div className="mb-3 text-center">
          <span className="text-3xl font-bold text-purple-800">{price}</span>
          {period && <span className="text-sm text-gray-500 ml-1">{period}</span>}
        </div>

        <p className="text-xs text-gray-600">{description}</p>

        {showBillingToggle && (
          <div className="text-xs text-gray-500 mt-2">
            <span
              onClick={(e) => {
                e.stopPropagation();
                onBillingToggle("monthly");
              }}
              className={`cursor-pointer mr-2 ${
                selectedBilling === "monthly" ? "font-semibold text-purple-700" : "text-gray-500 font-semibold"
              }`}
            >
              Monthly
            </span>
            |
            <span
              onClick={(e) => {
                e.stopPropagation();
                onBillingToggle("yearly");
              }}
              className={`cursor-pointer ml-2 ${
                selectedBilling === "yearly" ? "font-semibold text-purple-700" : "text-gray-500 font-semibold"
              }`}
            >
              Yearly (Save 2 months)
            </span>
          </div>
        )}
      </div>

      <div className="p-6 flex-grow">
        <div className="space-y-6">
          {initialFeatures.map(renderCategory)}
          {showAllFeatures && additionalFeatures.map(renderCategory)}
        </div>

        {showMoreFeatures && (
          <div className="mt-4 pt-2 border-t border-gray-100">
            <button 
              className="text-sm text-gray-500 hover:text-purple-600 transition-colors flex items-center justify-center w-full"
              onClick={(e) => {
                e.stopPropagation();
                setShowAllFeatures(!showAllFeatures);
              }}
            >
              {showAllFeatures ? (
                <>
                  Show Less Features
                  <ChevronUp size={16} className="ml-1" />
                </>
              ) : (
                <>
                  {showMoreFeatures}
                  <ChevronDown size={16} className="ml-1" />
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {!hideButtons && (
        <div className="mb-5 px-4">
          <button
            className={`w-full py-3 font-medium rounded-lg transition-all duration-300 cursor-pointer ${
              type === currentPlan
                ? "bg-gray-200 text-gray-600 cursor-not-allowed"
                : isSelected
                ? "bg-purple-600 text-white hover:bg-purple-700 shadow-lg"
                : type === "free"
                ? "bg-white text-purple-600 border-2 border-purple-600 hover:bg-purple-50"
                : "bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:from-purple-700 hover:to-indigo-700 shadow-lg"
            }`}
            disabled={type === currentPlan}
            onClick={() => type !== currentPlan && onClick(type)}
          >
            {getButtonText()}
          </button>
        </div>
      )}
    </div>
  );
};

export default SubscriptionCard;
